import { useCallback, useEffect, useRef, useState } from "react";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { fetchTrayVerdicts, saveTrayVerdict } from "./trayVerdictsApi";
import type {
  ApplyTrayVerdictHistoryChanges,
  TrayVerdicts,
  UseTrayVerdictsArgs,
  UseTrayVerdictsResult,
} from "./types";

const NO_VERDICTS: TrayVerdicts = {};
Object.freeze(NO_VERDICTS);

export const useTrayVerdicts = ({
  floorPlanId,
}: UseTrayVerdictsArgs): UseTrayVerdictsResult => {
  const [verdicts, setVerdicts] = useState<TrayVerdicts>(NO_VERDICTS);
  const [areVerdictsLoaded, setAreVerdictsLoaded] = useState(false);
  const [didSaveFail, setDidSaveFail] = useState(false);
  // Read by the writer so it does not depend on the map, and ruling on one type
  // does not rebuild the callback every row on the panel holds.
  const verdictsRef = useRef<TrayVerdicts>(NO_VERDICTS);
  verdictsRef.current = verdicts;

  /**
   * Writes his verdict and only then shows it.
   *
   * Same rule the walls follow: the router reads this from Postgres, so a
   * verdict on screen that the server never took is a type he would think he
   * had admitted and the cable would still refuse.
   */
  const setVerdict = useCallback(
    async (routeTypeId: number, carriesCable: boolean | null) => {
      if (!floorPlanId) return;
      setDidSaveFail(false);
      const saved = await saveTrayVerdict(routeTypeId, carriesCable);
      if (!saved) {
        setDidSaveFail(true);
        return;
      }
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_TRAY_VERDICT_SAVED, {
        carriesCable:
          carriesCable === null ? "default" : carriesCable ? "yes" : "no",
      });
      const next = { ...verdictsRef.current };
      if (carriesCable === null) delete next[routeTypeId];
      else next[routeTypeId] = carriesCable;
      setVerdicts(next);
    },
    [floorPlanId],
  );

  // Patched in place like the plan cache, never refetched.
  const applyHistoryChanges: ApplyTrayVerdictHistoryChanges = useCallback(
    ({ floorPlanId: changedFloorPlanId, changes }) => {
      if (!floorPlanId || changedFloorPlanId !== floorPlanId) {
        return;
      }
      let next: TrayVerdicts | null = null;
      for (const change of changes) {
        if (change.table !== "cableTrayVerdicts") {
          continue;
        }
        next = next ?? { ...verdictsRef.current };
        if (change.after !== null) {
          next[change.after.routeTypeId] = change.after.carriesCable;
        } else if (change.before !== null) {
          delete next[change.before.routeTypeId];
        }
      }
      if (next !== null) {
        setVerdicts(next);
      }
    },
    [floorPlanId],
  );

  useEffect(() => {
    if (!floorPlanId) {
      setVerdicts(NO_VERDICTS);
      setAreVerdictsLoaded(false);
      return;
    }
    let isCurrent = true;
    setAreVerdictsLoaded(false);
    const load = async () => {
      const loaded = await fetchTrayVerdicts(floorPlanId);
      // The plan may have changed while this was in flight.
      if (!isCurrent) return;
      // A read that failed stays UNLOADED rather than reading as "he has ruled
      // on nothing": the allowlist would silently fall back to the vocabulary he
      // overruled, and the panel would show his refusal as an admission.
      if (!loaded) return;
      setVerdicts(loaded);
      setAreVerdictsLoaded(true);
    };
    void load();
    return () => {
      isCurrent = false;
    };
  }, [floorPlanId]);

  return {
    verdicts,
    areVerdictsLoaded,
    didSaveFail,
    setVerdict,
    applyHistoryChanges,
  };
};
